import { useTranslation } from '../../../../shared/i18n';
import React, { useEffect, useRef, useState } from 'react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import 'xterm/css/xterm.css';
import { Alert, Spin, Button, Space } from 'antd';
import { ReloadOutlined, FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import hostService from '../../../../shared/services/hostService';

const HostTerminal = ({ containerId }) => {
    const { t } = useTranslation();
    const terminalRef = useRef(null);
    const xtermRef = useRef(null);
    const wsRef = useRef(null);
    const fitAddonRef = useRef(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [nodeInfo, setNodeInfo] = useState(null);

    // 向控制面申请一次性终端票据（60 秒，单次使用）。浏览器不直连节点：
    // 节点只有 :8080 明文且要求 X-API-Key，由控制面 WS 代理并注入密钥。
    const fetchNodeInfo = async () => {
        try {
            const response = await hostService.getTerminalTicket(containerId);
            if (response.success && response.data?.path) {
                setNodeInfo(response.data);
                return response.data;
            }
            throw new Error(response.error || t('Failed to retrieve terminal ticket'));
        } catch (err) {
            setError(t('Unable to open terminal: {{message}}', { message: err.message }));
            setLoading(false);
            return null;
        }
    };

    const connectWebSocket = (term, nodeData) => {
        if (!nodeData || !nodeData.path) {
            setError(t('Invalid terminal ticket'));
            setLoading(false);
            return;
        }

        const apiHost = process.env.REACT_APP_API_BASE_URL || 'https://api2.gtm.cx';
        const wsProtocol = apiHost.startsWith('https') ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${apiHost.replace(/^https?:\/\//, '')}${nodeData.path}`;

        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                setLoading(false);
                setConnected(true);
                setError(null);
                term.writeln('\x1b[32mConnected to container terminal\x1b[0m');
                term.writeln('');

                // 发送初始尺寸
                const { rows, cols } = term;
                ws.send(JSON.stringify({
                    type: 'resize',
                    rows,
                    cols,
                }));
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'output') {
                        term.write(data.data);
                    } else if (data.type === 'error') {
                        term.writeln(`\r\n\x1b[31mError: ${data.message}\x1b[0m`);
                    }
                } catch (e) {
                    // 如果不是JSON，作为原始输出处理
                    term.write(event.data);
                }
            };

            ws.onerror = () => {
                setError(t('WebSocket connection error'));
                setLoading(false);
                setConnected(false);
                term.writeln('\r\n\x1b[31m' + t('Connection error') + '\x1b[0m');
            };

            ws.onclose = () => {
                setConnected(false);
                term.writeln('\r\n\x1b[33m' + t('Connection closed') + '\x1b[0m');
            };

            // 处理终端输入
            term.onData((data) => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({
                        type: 'input',
                        data,
                    }));
                }
            });
        } catch (err) {
            setError(t('Connection failed: {{message}}', { message: err.message }));
            setLoading(false);
        }
    };

    const initTerminal = async () => {
        if (!containerId) {
            setError(t('Missing container ID'));
            setLoading(false);
            return;
        }

        // 清理旧连接
        if (wsRef.current) {
            wsRef.current.close();
        }
        if (xtermRef.current) {
            xtermRef.current.dispose();
        }

        setLoading(true);
        setError(null);

        // 获取节点信息
        const nodeData = await fetchNodeInfo();
        if (!nodeData) return;

        // 初始化 xterm.js
        const term = new XTerm({
            cursorBlink: true,
            fontSize: 14,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            theme: {
                background: '#1e1e1e',
                foreground: '#d4d4d4',
                cursor: '#d4d4d4',
                black: '#000000',
                red: '#cd3131',
                green: '#0dbc79',
                yellow: '#e5e510',
                blue: '#2472c8',
                magenta: '#bc3fbc',
                cyan: '#11a8cd',
                white: '#e5e5e5',
                brightBlack: '#666666',
                brightRed: '#f14c4c',
                brightGreen: '#23d18b',
                brightYellow: '#f5f543',
                brightBlue: '#3b8eea',
                brightMagenta: '#d670d6',
                brightCyan: '#29b8db',
                brightWhite: '#e5e5e5',
            },
            rows: 30,
            cols: 120,
        });

        const fitAddon = new FitAddon();
        const webLinksAddon = new WebLinksAddon();

        term.loadAddon(fitAddon);
        term.loadAddon(webLinksAddon);

        if (terminalRef.current) {
            term.open(terminalRef.current);
            fitAddon.fit();
        }

        xtermRef.current = term;
        fitAddonRef.current = fitAddon;

        // 连接WebSocket
        connectWebSocket(term, nodeData);
    };

    useEffect(() => {
        initTerminal();

        // 处理窗口大小变化
        const handleResize = () => {
            if (fitAddonRef.current) {
                fitAddonRef.current.fit();
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && xtermRef.current) {
                    const { rows, cols } = xtermRef.current;
                    wsRef.current.send(JSON.stringify({
                        type: 'resize',
                        rows,
                        cols,
                    }));
                }
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (xtermRef.current) {
                xtermRef.current.dispose();
            }
        };
    }, [containerId]);

    const handleReconnect = () => {
        initTerminal();
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
        setTimeout(() => {
            if (fitAddonRef.current) {
                fitAddonRef.current.fit();
            }
        }, 100);
    };

    return (
        <div style={{
            height: fullscreen ? '100vh' : '500px',
            display: 'flex',
            flexDirection: 'column',
            position: fullscreen ? 'fixed' : 'relative',
            top: fullscreen ? 0 : 'auto',
            left: fullscreen ? 0 : 'auto',
            right: fullscreen ? 0 : 'auto',
            bottom: fullscreen ? 0 : 'auto',
            zIndex: fullscreen ? 1000 : 'auto',
            backgroundColor: '#1e1e1e',
        }}>
            {/* 工具栏 */}
            <div style={{
                padding: '8px 12px',
                backgroundColor: '#2d2d2d',
                borderBottom: '1px solid #404040',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Space>
                    <span style={{ color: connected ? '#52c41a' : '#ff4d4f', fontSize: 12 }}>
                        {connected ? t('Connected') : t('Not connected')}
                    </span>
                    {nodeInfo?.apiDomain && (
                        <span style={{ color: '#888', fontSize: 12 }}>
                            @ {nodeInfo.apiDomain}
                        </span>
                    )}
                </Space>
                <Space>
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={handleReconnect}
                        style={{ color: '#d4d4d4', borderColor: '#404040', backgroundColor: 'transparent' }}
                    >
                        {t('Reconnect')}
                    </Button>
                    <Button
                        size="small"
                        icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                        onClick={toggleFullscreen}
                        style={{ color: '#d4d4d4', borderColor: '#404040', backgroundColor: 'transparent' }}
                    >
                        {fullscreen ? t('Exit full screen') : t('Full screen')}
                    </Button>
                </Space>
            </div>

            {/* 加载状态 */}
            {loading && (
                <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#1e1e1e' }}>
                    <Spin tip={t('Connecting to terminal...')} />
                </div>
            )}

            {/* 错误提示 */}
            {error && (
                <Alert
                    message={t('Terminal error')}
                    description={error}
                    type="error"
                    showIcon
                    style={{ margin: '10px' }}
                    action={
                        <Button size="small" onClick={handleReconnect}>
                            {t('Retry')}
                        </Button>
                    }
                />
            )}

            {/* 终端容器 */}
            <div
                ref={terminalRef}
                style={{
                    flex: 1,
                    backgroundColor: '#1e1e1e',
                    padding: '10px',
                    overflow: 'hidden',
                }}
            />
        </div>
    );
};

export default HostTerminal;
